import { useLazyQuery, useMutation } from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import {
    GetBPApplicationStatus,
    GetProjectBulkAddStatus,
} from 'api/graphQL/queries/projectBulk.queries'
import { BackendProcessStatus } from 'api/graphQL/types/backendProcess.types'
import {
    ProjectBulkAddResult,
    ProjectBulkAddStatus,
} from 'api/graphQL/types/projectBulk.types'
import { processEnv } from 'components/configuration/environmentVariables'
import React from 'react'

/**
 * Custom hook to track Best Practice application progress and child element addition.
 * Handles polling backend status and showing progress dialog.
 *
 * @param bestPracticeAutomationInstanceId - Instance ID for BP application
 * @param childElementsInstanceId - Instance ID for child element addition (sequential before BP)
 * @param onComplete - Optional callback when ALL processes complete
 * @param onChildAddComplete - Optional callback when child element addition completes, receives the result data
 * @returns Object containing BP progress state and dialog props
 */
export const useBPTracking = (
    bestPracticeAutomationInstanceId?: string,
    childElementsInstanceId?: string,
    onComplete?: () => void,
    onChildAddComplete?: (result?: Partial<ProjectBulkAddResult>) => void
) => {
    const [bpProgress, setBpProgress] = React.useState<number>(0)
    const [bpInProgress, setBpInProgress] = React.useState(false)
    const [bpInstance, setBpInstance] = React.useState<string>()

    const [childProgress, setChildProgress] = React.useState<number>(0)
    const [childInProgress, setChildInProgress] = React.useState(false)
    const [childInstance, setChildInstance] = React.useState<string>()

    const [currentPhase, setCurrentPhase] = React.useState<
        'CHILD_ADD' | 'BP_APPLICATION' | null
    >(null)

    const isMountedRef = React.useRef(true)

    // Guards against the data from a previous run being processed
    // before the first fresh response arrives for the current instance.
    const bpAwaitingFirstResponseRef = React.useRef(false)
    const childAwaitingFirstResponseRef = React.useRef(false)

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    // Cleanup on unmount
    React.useEffect(() => {
        return () => {
            isMountedRef.current = false
            setBpInProgress(false)
            setChildInProgress(false)
        }
    }, [])

    // Child element addition status checking query
    const [
        getChildStatus,
        { data: childStatusData, error: childStatusError },
    ] = useLazyQuery<{
        getProjectBulkAddStatus: Partial<ProjectBulkAddStatus>
    }>(GetProjectBulkAddStatus, {
        onCompleted: (data) => {
            if (!isMountedRef.current) return
            childAwaitingFirstResponseRef.current = false
            setChildProgress(
                data?.getProjectBulkAddStatus?.progressPercentage ?? 0
            )
        },
        onError: (error) => {
            if (!isMountedRef.current) return
            childAwaitingFirstResponseRef.current = false
            setChildInProgress(false)
            setChildProgress(0)
            console.error('Child element addition failed:', error.message)

            showGlobalSnackbar({
                variables: {
                    message: 'Failed to add child elements. Please try again.',
                    variant: 'error',
                    id: 'ChildElementAddError',
                },
            })

            if (bpInstance) {
                setCurrentPhase('BP_APPLICATION')
                setBpInProgress(true)
                bpAwaitingFirstResponseRef.current = true
                getStatus({
                    variables: {
                        input: { instanceId: bpInstance },
                    },
                })
            }
        },
        fetchPolicy: 'no-cache',
    })

    // BP status checking query
    const [getStatus, { data: statusData, error: statusError }] = useLazyQuery<{
        getBestPracticeApplicationStatus: Partial<BackendProcessStatus>
    }>(GetBPApplicationStatus, {
        onCompleted: (data) => {
            if (!isMountedRef.current) return
            bpAwaitingFirstResponseRef.current = false
            setBpProgress(
                data?.getBestPracticeApplicationStatus?.progressPercentage ?? 0
            )
        },
        onError: (error) => {
            if (!isMountedRef.current) return
            bpAwaitingFirstResponseRef.current = false
            setBpInProgress(false)
            setBpProgress(0)
            showGlobalSnackbar({
                variables: {
                    message: `Failed to check Best Practice status: ${error.message}`,
                    variant: 'error',
                    id: 'BPApplicationStatusError',
                },
            })
        },
        fetchPolicy: 'no-cache',
    })

    // Initialize child element instance when provided
    React.useEffect(() => {
        if (!childElementsInstanceId) return
        if (!(processEnv.REACT_APP_EnableSmartProjectCopy === '1')) return
        setChildInstance(childElementsInstanceId)
    }, [childElementsInstanceId])

    // Start polling when instanceId is provided
    React.useEffect(() => {
        if (!bestPracticeAutomationInstanceId) return
        if (!(processEnv.REACT_APP_EnableSmartProjectCopy === '1')) return

        setBpInstance(bestPracticeAutomationInstanceId)
    }, [bestPracticeAutomationInstanceId])

    // Determine which process to start
    React.useEffect(() => {
        if (!isMountedRef.current) return
        if (currentPhase) return
        if (!(processEnv.REACT_APP_EnableSmartProjectCopy === '1')) return

        // Priority 1: Start with child add if available
        if (childInstance && !childInProgress && !bpInProgress) {
            setCurrentPhase('CHILD_ADD')
            setChildInProgress(true)
            childAwaitingFirstResponseRef.current = true
            getChildStatus({
                variables: {
                    input: { instanceId: childInstance },
                },
            })
            return
        }

        // Priority 2: Start BP if no child add OR child add is done
        if (bpInstance && !bpInProgress && !childInProgress) {
            setCurrentPhase('BP_APPLICATION')
            setBpInProgress(true)
            bpAwaitingFirstResponseRef.current = true
            getStatus({
                variables: {
                    input: { instanceId: bpInstance },
                },
            })
        }
    }, [
        childInstance,
        bpInstance,
        childInProgress,
        bpInProgress,
        currentPhase,
        getChildStatus,
        getStatus,
    ])

    // Polling loop for child element addition
    React.useEffect(() => {
        if (!(processEnv.REACT_APP_EnableSmartProjectCopy === '1')) return
        if (currentPhase !== 'CHILD_ADD') return
        if (childAwaitingFirstResponseRef.current) return
        if (!childStatusData && !childStatusError) return
        if (!isMountedRef.current) {
            setChildInProgress(false)
            return
        }

        const state = childStatusData?.getProjectBulkAddStatus?.state

        if (state === 'Complete') {
            setChildInProgress(false)
            setChildProgress(0)
            setChildInstance(undefined)

            // Notify child completion with result data (for UI update)
            const result = childStatusData?.getProjectBulkAddStatus?.result
            onChildAddComplete?.(result ?? undefined)

            if (
                bpInstance &&
                processEnv.REACT_APP_EnableSmartProjectCopy === '1'
            ) {
                setCurrentPhase('BP_APPLICATION')
                setBpInProgress(true)
                bpAwaitingFirstResponseRef.current = true
                getStatus({
                    variables: {
                        input: { instanceId: bpInstance },
                    },
                })
            } else {
                setCurrentPhase(null)
                onComplete?.()
            }
            return
        }

        if (childStatusError || state === 'Error') {
            setChildInProgress(false)
            setChildProgress(0)
            setChildInstance(undefined)

            console.error('Child element addition failed')

            showGlobalSnackbar({
                variables: {
                    message:
                        'Child element addition failed. Continuing with Best Practices application.',
                    variant: 'warning',
                    id: 'ChildElementAddFailure',
                },
            })

            if (
                bpInstance &&
                processEnv.REACT_APP_EnableSmartProjectCopy === '1'
            ) {
                setCurrentPhase('BP_APPLICATION')
                setBpInProgress(true)
                bpAwaitingFirstResponseRef.current = true
                getStatus({
                    variables: {
                        input: { instanceId: bpInstance },
                    },
                })
            } else {
                setCurrentPhase(null)
            }
            return
        }

        if (
            childInProgress &&
            isMountedRef.current &&
            state !== 'Complete' &&
            state !== 'Error'
        ) {
            const queryTimeout = setTimeout(() => {
                if (isMountedRef.current && childInProgress && childInstance) {
                    getChildStatus({
                        variables: {
                            input: { instanceId: childInstance },
                        },
                    })
                }
            }, 1000)
            return () => clearTimeout(queryTimeout)
        }
    }, [
        childStatusData,
        childStatusError,
        childInProgress,
        currentPhase,
        bpInstance,
        onComplete,
        getStatus,
        getChildStatus,
    ])

    React.useEffect(() => {
        if (!(processEnv.REACT_APP_EnableSmartProjectCopy === '1')) return
        if (currentPhase !== 'BP_APPLICATION') return
        if (bpAwaitingFirstResponseRef.current) return
        if (!statusData && !statusError) return
        if (!isMountedRef.current) {
            setBpInProgress(false)
            return
        }

        const state = statusData?.getBestPracticeApplicationStatus?.state

        // Check if process is complete
        if (state === 'Complete') {
            setBpInProgress(false)
            setBpProgress(0)
            showGlobalSnackbar({
                variables: {
                    message: 'Best Practices have successfully been applied!',
                    variant: 'success',
                    id: 'BestPracticesAppliedSuccess',
                },
            })
            // Clear instance to allow new BP processes
            setBpInstance(undefined)
            setCurrentPhase(null)

            // Execute completion callback if provided
            if (onComplete) {
                onComplete()
            }
            return
        }

        // Check if process errored
        if (statusError || state === 'Error') {
            setBpInProgress(false)
            setBpProgress(0)
            showGlobalSnackbar({
                variables: {
                    message:
                        'Best Practice application failed. Please try again.',
                    variant: 'error',
                    id: 'BestPracticesApplicationError',
                },
            })
            setBpInstance(undefined)
            setCurrentPhase(null)
            return
        }

        // Only continue polling if still in progress
        if (
            bpInProgress &&
            isMountedRef.current &&
            state !== 'Complete' &&
            state !== 'Error'
        ) {
            // Poll again after 3 seconds
            const queryTimeout = setTimeout(() => {
                if (isMountedRef.current && bpInProgress && bpInstance) {
                    getStatus({
                        variables: {
                            input: { instanceId: bpInstance },
                        },
                    })
                }
            }, 3000)
            return () => {
                clearTimeout(queryTimeout)
            }
        }
    }, [
        statusData,
        statusError,
        bpInProgress,
        currentPhase,
        showGlobalSnackbar,
        getStatus,
        onComplete,
    ])

    const progressDialogProps = React.useMemo(() => {
        const isAnyInProgress = childInProgress || bpInProgress
        if (!isAnyInProgress) return null

        // Feature flag check
        if (!(processEnv.REACT_APP_EnableSmartProjectCopy === '1')) return null
        let dialogName = ''
        let statusText = ''
        let currentProgress = 0

        // Show child add if it's running, otherwise show BP
        if (currentPhase === 'CHILD_ADD') {
            dialogName = 'Adding Elements'
            statusText =
                childProgress > 0
                    ? `Adding Best Practice child elements to your project...`
                    : 'Preparing to add Best Practice child elements...'
            currentProgress = childProgress
        } else if (currentPhase === 'BP_APPLICATION') {
            dialogName = 'Applying Best Practices'
            statusText =
                bpProgress > 0
                    ? `Applying Best Practices to the elements...`
                    : 'Preparing to apply Best Practices...'
            currentProgress = bpProgress
        }

        return {
            dialogId: 'background-process-progress-dialog',
            dialogName: dialogName,
            open: true,
            statusText: statusText,
            progressPercentage: Math.round(currentProgress),
            variant: 'Add Family' as const,
            showProgressPercentage: currentProgress > 0,
        }
    }, [childInProgress, bpInProgress, childProgress, bpProgress, currentPhase])

    return {
        bpProgress,
        bpInProgress: childInProgress || bpInProgress,
        progressDialogProps,
    }
}
